import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getFirestore, initializeFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'

const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'demo-yokoso-pay'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: `${projectId}.firebaseapp.com`,
  projectId,
  storageBucket: `${projectId}.firebasestorage.app`,
}

const app = initializeApp(firebaseConfig)

export const auth = getAuth(app)
// Firebase が送信する verification / password reset / email change 等の認証
// メールの言語を JP 既定に設定する (#846 follow-up)。これを設定しないと
// 英語テンプレが使われ、店舗オーナー(JP 想定 / CLAUDE.md user 層 bimodal
// メモリ参照)に意味不明な英語メールが届く問題があった。
// 顧客側 (10 ロケール) は Google ログイン主体で verification mail が
// 飛ぶ頻度は低いが、将来動的切替が必要になれば useI18n の locale を
// `auth.languageCode` に流し込む形で拡張可能。
auth.languageCode = 'ja'
const firestoreDatabase = import.meta.env.VITE_FIRESTORE_DATABASE || '(default)'
export const firestoreDb = firestoreDatabase === '(default)'
  ? getFirestore(app)
  : initializeFirestore(app, {}, firestoreDatabase)
export const storage = getStorage(app)

// スマホ実機確認 (Tailscale 等) では「localhost」はその端末自身を指すため、
// localhost 指定の emulator URL をページのホスト名・プロトコルに置き換える。
// PC (localhost) からは無変換。tailscale serve が同ポートで TLS 提供する前提
// (例: https://<host>.ts.net:5173 で開いたら auth は https://<host>.ts.net:9099)。
function followPageHost(url: string): string {
  const page = window.location
  // *.localhost (例: #2140 体験の trial-store.localhost) もブラウザが自分の PC に向けるので、
  // localhost のまま繋げばよい。書き換えると dev の CSP (connect-src http://localhost:*) に弾かれ、
  // 認証が auth/network-request-failed になる (リアルタイム更新が polling に落ちる)
  if (page.hostname === 'localhost' || page.hostname.endsWith('.localhost') || page.hostname === '127.0.0.1') return url
  try {
    const u = new URL(url)
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      u.hostname = page.hostname
      u.protocol = page.protocol
    }
    return u.toString()
  } catch {
    return url
  }
}

// Connect to emulators in development
if (import.meta.env.DEV && import.meta.env.VITE_FIRESTORE_EMULATOR_HOST) {
  const authUrl = followPageHost(
    import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL || 'http://localhost:9099',
  )
  connectAuthEmulator(auth, authUrl, { disableWarnings: true })

  const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || 'localhost'
  const firestorePort = parseInt(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || '8080')
  connectFirestoreEmulator(firestoreDb, firestoreHost, firestorePort)

  // #1881 ポートは worktree ごとにずれるので env から取る。**固定値だと
  // slot 1 以外で画像アップロードが繋がらず、エラーにもならず永久に再試行する。**
  const storagePort = parseInt(import.meta.env.VITE_STORAGE_EMULATOR_PORT || '9199')
  connectStorageEmulator(storage, 'localhost', storagePort)

  console.log('Firebase Emulator connected')
}

export default app

/**
 * #1641 リアルタイム購読専用の 2 つめの Firebase アプリ。
 *
 * 注文の購読は「その注文の当事者」であることをトークンで示す必要があるが、
 * **既定のアプリのログインを置き換えてはいけない**:
 *
 * - スタッフ端末では、同じブラウザにオーナーがログインしていることがある
 *   (スタッフは PIN のセッション、オーナーは Firebase)。既定のアプリで
 *   スタッフとしてサインインすると、オーナーの API 呼び出しが壊れる
 * - ゲストを既定のアプリで匿名サインインさせると `auth.currentUser` が埋まり、
 *   apiFetch がメールの無いトークンを送ってしまう (サーバはメールを要求するので 401)
 *
 * そこで購読専用のアプリを分ける。ログイン中のお客様とオーナーは既定のアプリの
 * まま (クレームを足すだけ) なので、ここを使うのはスタッフとゲストだけ。
 */
let realtimeParts: { app: FirebaseApp; auth: Auth; db: Firestore } | null = null

export function getRealtimeFirebase(): { auth: Auth; db: Firestore } {
  if (!realtimeParts) {
    const app = initializeApp(firebaseConfig, 'realtime')
    const realtimeAuth = getAuth(app)
    const db = firestoreDatabase === '(default)'
      ? getFirestore(app)
      : initializeFirestore(app, {}, firestoreDatabase)
    if (import.meta.env.DEV && import.meta.env.VITE_FIRESTORE_EMULATOR_HOST) {
      connectAuthEmulator(
        realtimeAuth,
        followPageHost(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL || 'http://localhost:9099'),
        { disableWarnings: true },
      )
      connectFirestoreEmulator(
        db,
        import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || 'localhost',
        parseInt(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || '8080'),
      )
    }
    realtimeParts = { app, auth: realtimeAuth, db }
  }
  return { auth: realtimeParts.auth, db: realtimeParts.db }
}
